import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from '@emotion/styled'
import SwipeableViews from 'react-swipeable-views'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { Button } from '@mobi/component-library/Base/Buttons/Button'
import { colors, layering, radius, shadow, spacing } from '@mobi/component-library/Foundations'
import { Pagination } from '@core/Components/Pagination'
import { PreviewTipsEvent, previewTipsTracker } from '../RaceContent/helpers/analytics'

const enum LocalConstants {
  CarouselButtonHiddenClassname = 'tips-panel-carousel__button--hidden',
}

interface Props {
  children: React.ReactNode
  previewTipsEventData?: PreviewTipsEvent[]
}

export const TipsPanelCarousel = ({ children, previewTipsEventData }: Props): JSX.Element => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [slideHeights, setSlideHeights] = useState<Record<number, number>>({})
  const slides = useMemo(() => [...React.Children.toArray(children)], [children])
  const showSlidePercent = useResponsivePadding()

  const handleHeightChange = useCallback((index: number, height: number) => {
    setSlideHeights(prev => {
      if (prev[index] === height) return prev
      return { ...prev, [index]: height }
    })
  }, [])

  useEffect(() => {
    if (!previewTipsEventData || !previewTipsEventData[activeIndex]) return

    previewTipsTracker({
      interaction_type: 'View Content',
      author: previewTipsEventData[activeIndex].author || '',
      raceCode: previewTipsEventData[activeIndex].raceCode || '',
      meetingName: previewTipsEventData[activeIndex].meetingName || '',
      raceStatus: previewTipsEventData[activeIndex].raceStatus,
    })
  }, [activeIndex, slides, previewTipsEventData])

  return (
    <WrapperStyled
      className={[
        activeIndex === 0 ? `${LocalConstants.CarouselButtonHiddenClassname}--left` : '',
        activeIndex === slides.length - 1
          ? `${LocalConstants.CarouselButtonHiddenClassname}--right`
          : '',
      ].join(' ')}
    >
      <div className='no-vertical-scroll'>
        <Button
          color='secondary_outline'
          className={activeIndex === 0 ? LocalConstants.CarouselButtonHiddenClassname : ''}
          type='button'
          onClick={() => setActiveIndex(index => Math.max(index - 1, 0))}
          disabled={activeIndex === 0}
        >
          <Icon name='SolidChevronLeft' size='2rem' color={colors.studio[500]} />
        </Button>

        <Button
          color='secondary_outline'
          className={
            activeIndex === slides.length - 1 ? LocalConstants.CarouselButtonHiddenClassname : ''
          }
          type='button'
          onClick={() => setActiveIndex(index => Math.min(index + 1, slides.length - 1))}
          disabled={activeIndex === slides.length - 1}
        >
          <Icon name='SolidChevronRight' size='2rem' color={colors.studio[500]} />
        </Button>
        <SwipeableViews
          enableMouseEvents={true}
          index={activeIndex}
          onChangeIndex={setActiveIndex}
          containerStyle={{ willChange: 'auto' }}
          style={{
            height: slideHeights[activeIndex],
            transition: 'height 0.35s ease-in-out',
            overflow: 'hidden',
            padding:
              slides.length === 1
                ? '0'
                : activeIndex === 0
                  ? `0 ${showSlidePercent} 0 0`
                  : activeIndex === slides.length - 1
                    ? `0 0 0 ${showSlidePercent}`
                    : `0 ${showSlidePercent} 0 ${showSlidePercent}`,

            paddingRight: slides.length === 1 ? '1.6rem' : showSlidePercent,
          }}
        >
          {slides.map((slide, i) => (
            <SlideHeightObserver key={i} index={i} onHeightChange={handleHeightChange}>
              <div data-active={activeIndex === i}>{slide}</div>
            </SlideHeightObserver>
          ))}
        </SwipeableViews>
      </div>
      <Pagination activeColor={colors.studio[500]} dotCount={slides.length} index={activeIndex} />
    </WrapperStyled>
  )
}

const SlideHeightObserver = ({
  children,
  index,
  onHeightChange,
}: {
  children: React.ReactNode
  index: number
  onHeightChange: (index: number, height: number) => void
}) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!ref.current) return
    const observer = new ResizeObserver(entries => {
      const height = entries[0]?.contentRect.height
      if (height) onHeightChange(index, height)
    })
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [index, onHeightChange])

  return (
    <div data-js-race-card-no-swipe ref={ref}>
      {children}
    </div>
  )
}

const useResponsivePadding = () => {
  const [percent, setPercent] = useState(getPercent())

  function getPercent() {
    if (window.innerWidth < 600) return '5%'
    return '8%'
  }

  useEffect(() => {
    function handleResize() {
      setPercent(getPercent())
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return percent
}

// Styles
const WrapperStyled = styled.div({
  '.react-swipeable-view-container': {
    gap: spacing.smx1,
    paddingLeft: '1.6rem',
  },

  '@media (max-width: 450px)': {
    '[data-testid^="tip-button-"]': {
      columnGap: '0',
      padding: '1rem 0.3rem',
    },
  },

  '[data-testid^="tip-button-"]': {
    img: {
      width: 'auto',
      height: 'auto',
      objectFit: 'unset',
    },

    '@media (max-width: 400px)': {
      img: {
        width: '100%',
        height: 'auto',
        objectFit: 'contain',
      },
    },
  },

  "[data-active='false'] [data-testid='new-race-content-preview-text']": {
    display: 'none !important',
  },

  '> div': {
    background: colors.surface[400],
    position: 'relative',

    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      width: '6rem',
      height: '100%',
      pointerEvents: 'none',
      zIndex: layering.overlayLow,
      background: 'linear-gradient(to left, rgba(255,255,255,.8), rgba(255,255,255,0))',
    },

    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '6rem',
      height: '100%',
      pointerEvents: 'none',
      zIndex: layering.overlayLow,
      background: 'linear-gradient(to right, rgba(255,255,255,.8), rgba(255,255,255,0))',
    },

    '@media (pointer: coarse)': {
      '&::after': { display: 'none' },
      '&::before': { display: 'none' },
    },

    '> button': {
      position: 'absolute',
      top: '45%',
      zIndex: layering.overlayHigh,
      borderRadius: radius.full,
      height: '4.2rem',
      width: '4.2rem',
      cursor: 'pointer',
      border: `0.05rem solid ${colors.neutral[300]}`,
      boxShadow: shadow.lg,
      background: colors.white,

      '@media (pointer: coarse)': {
        display: 'none',
      },
    },

    '> button:nth-of-type(1)': {
      left: 8,
    },

    '> button:nth-of-type(2)': {
      right: 8,
    },

    [`> button.${LocalConstants.CarouselButtonHiddenClassname}`]: {
      visibility: 'hidden',
    },
  },

  [`&.${LocalConstants.CarouselButtonHiddenClassname}--left > div::before`]: {
    display: 'none',
  },

  [`&.${LocalConstants.CarouselButtonHiddenClassname}--right > div::after`]: {
    display: 'none',
  },
})
